import { i18nNsFactory } from '@atlas-frontend/i18n';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { Button, Flex } from 'antd';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Table } from '../../common/tanstacktable';
import {
  suppressHref,
  useHrefWithQueryParams,
} from '../../common/useHrefWithQueryParams';
import { useDataObjectContext } from '../DataObjectContext';
import { AlternativeAlgorithmSetDataType } from '../schema';
import translations from '../translations/AlternativeAlgorithmsViewTable.translations.json';
import { usePanelParams } from '../usePanelParams';
import { useTableFilter } from '../useTableFilter';
import { AlternativeAlgorithmViewPanel } from './AlternativeAlgorithmViewPanel';
import { AlternativesCell } from './AlternativesCell';
import { alternativeFilterFn, getExceptionsCount } from './utils';
import { AlternativeAlgorithmViewModal } from './AlternativeAlgorithmViewModal';
import { useListAlgorithmsQuery } from '../algorithms/query/useGetAlgorithmsQuery';
import { ViewableGroupOrAlgorithm } from './types';

const { addTranslations, useTranslation } = i18nNsFactory(
  'alternatives-view-table',
);
addTranslations(translations);
const columnHelper = createColumnHelper<AlternativeAlgorithmSetDataType>();

export interface AlternativeAlgorithmsViewTableProps {
  dataObjectVersionId: number;
  data: AlternativeAlgorithmSetDataType[];
}

export function AlternativeAlgorithmsViewTable({
  dataObjectVersionId,
  data,
}: AlternativeAlgorithmsViewTableProps) {
  const { t } = useTranslation();
  const [
    { alternativeAlgorithmSetId, alternativeAlgorithmSetIndex },
    setPanelParams,
  ] = usePanelParams();

  const viewable = useMemo(
    () =>
      alternativeAlgorithmSetId
        ? data.find(({ id }) => id === Number(alternativeAlgorithmSetId))
        : data[Number(alternativeAlgorithmSetIndex)],
    [data, alternativeAlgorithmSetId, alternativeAlgorithmSetIndex],
  );

  const [viewableGroupOrAlgorithm, setViewableGroupOrAlgorithm] = useState<
    ViewableGroupOrAlgorithm | undefined
  >();
  const viewRow = useCallback(
    (alternativeAlgorithmSetIndex: number) =>
      setPanelParams({ alternativeAlgorithmSetIndex }),
    [],
  );
  const { setAlternativeFilters } = useDataObjectContext();
  const { columnFilters, setColumnFilters } = useTableFilter(
    setAlternativeFilters,
  );
  const { data: algorithms = [] } = useListAlgorithmsQuery({
    dataObjectVersionId,
    isAlgorithmView: true,
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('code', {
        header: t('column.code.label'),
        cell: CodeCell,
        meta: {
          width: 100,
          className: 'sticky-left',
        },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('name', {
        header: t('column.name.label'),
        filterFn: 'includesString',
        meta: {
          width: 250,
        },
      }),
      columnHelper.accessor('alternativeAlgorithms', {
        header: t('column.included.label'),
        filterFn: alternativeFilterFn,
        cell: (props) => (
          <AlternativeAlgorithmsCell
            {...props}
            onSelectGroupOrAlgorithm={setViewableGroupOrAlgorithm}
          />
        ),
        meta: {
          width: 250,
        },
      }),
      columnHelper.accessor(getExceptionsCount, {
        id: 'exceptionsCount',
        header: t('column.exceptions.label'),
        filterFn: 'includesString',
        cell: ExceptionsCountCell,
        meta: {
          width: 100,
          textAlign: 'right',
        },
      }),
    ],
    [t, viewRow],
  );
  const onCloseViewPanel = useCallback(
    () => setPanelParams(),
    [setPanelParams],
  );

  return (
    <Flex vertical align="end" gap="middle">
      <Table
        name="alternatives-view-table"
        data={data || []}
        columns={columns}
        filters={columnFilters}
        onFilter={setColumnFilters}
        defaultSort={[{ id: 'code', desc: false }]}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
      {viewable && (
        <AlternativeAlgorithmViewPanel
          details={viewable}
          onClose={onCloseViewPanel}
          onSelectGroupOrAlgorithm={setViewableGroupOrAlgorithm}
        />
      )}
      {viewableGroupOrAlgorithm && (
        <AlternativeAlgorithmViewModal
          algorithms={algorithms}
          viewable={viewableGroupOrAlgorithm}
          onClose={() => setViewableGroupOrAlgorithm(undefined)}
          isEditMode={false}
        />
      )}
    </Flex>
  );
}

const CodeCell: FC<CellContext<AlternativeAlgorithmSetDataType, any>> = memo(
  (props) => {
    const [, setPanelParams] = usePanelParams();
    const alternativeAlgorithmSetId = props.row.original.id;
    const alternativeAlgorithmSetIndex = props.row.index;
    const params = alternativeAlgorithmSetId
      ? { alternativeAlgorithmSetId }
      : { alternativeAlgorithmSetIndex };
    return (
      <StyledButton
        id={`edit-alternative-${props.row.index}`}
        href={useHrefWithQueryParams(params)}
        onClick={suppressHref(() => setPanelParams(params))}
        shape="round"
        type="link"
      >
        {props.renderValue()}
      </StyledButton>
    );
  },
);

const AlternativeAlgorithmsCell: FC<
  CellContext<AlternativeAlgorithmSetDataType, any> & {
    onSelectGroupOrAlgorithm: (viewable: ViewableGroupOrAlgorithm) => void;
  }
> = memo((props) => {
  const alternatives = props.renderValue() ?? [];
  return (
    <AlternativesCell
      alternatives={alternatives}
      alternativeCode={props.row.original.code}
      onSelectGroupOrAlgorithm={props.onSelectGroupOrAlgorithm}
    />
  );
});

const ExceptionsCountCell: FC<
  CellContext<AlternativeAlgorithmSetDataType, any>
> = memo((props) => {
  const count = props.renderValue();
  return count ? count.toString() : '';
});

const StyledButton = styled(Button)`
  margin-block: -0.5em;
  padding-inline: 0.75em;
`;
